import React from 'react';
import PropTypes from 'prop-types';

/**
 * Skeleton-Bausteine des Design-Systems (siehe MODERNIZATION_PLAN.md P1.3).
 *
 * Vorbild: SessionCardSkeleton.js (animate-pulse + aria-hidden). Ersetzt
 * Full-Page-Spinner in Listen-/Detail-Views durch Layout-treue Platzhalter.
 * Der ganze Block ist aria-hidden — umschließende Liste sollte selbst
 * aria-busy="true" + einen sr-only "Lädt…"-Text tragen.
 */

const Line = ({ className = '', width = 'w-full' }) => (
  <div className={`h-3.5 rounded bg-surface-raised animate-pulse ${width} ${className}`} aria-hidden="true" />
);
Line.propTypes = { className: PropTypes.string, width: PropTypes.string };

const Circle = ({ size = 'h-10 w-10', className = '' }) => (
  <div className={`rounded-full bg-surface-raised animate-pulse ${size} ${className}`} aria-hidden="true" />
);
Circle.propTypes = { size: PropTypes.string, className: PropTypes.string };

/** Card-Skeleton im Stil von SessionCardSkeleton: Badge-Zeile, Titel, 2 Textzeilen, Footer-Balken. */
const CardSkeleton = ({ className = '' }) => (
  <div
    data-testid="skeleton-card"
    className={`bg-surface/80 rounded-xl shadow-token-md border border-border overflow-hidden animate-pulse ${className}`}
    aria-hidden="true"
  >
    <div className="p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="h-6 w-16 rounded-full bg-surface-raised" />
        <div className="h-6 w-12 rounded-full bg-surface-raised" />
      </div>
      <div className="h-5 w-2/3 rounded bg-surface-raised" />
      <div className="space-y-2">
        <div className="h-3.5 w-full rounded bg-surface-raised" />
        <div className="h-3.5 w-4/5 rounded bg-surface-raised" />
      </div>
      <div className="h-10 w-full rounded-xl bg-surface-raised" />
    </div>
  </div>
);
CardSkeleton.propTypes = { className: PropTypes.string };

/** Zeile im Listen-Stil: Avatar links, zwei Textzeilen rechts (z.B. Chat-/Nutzerlisten). */
const RowSkeleton = ({ className = '' }) => (
  <div
    data-testid="skeleton-row"
    className={`flex items-center gap-3 p-3 animate-pulse ${className}`}
    aria-hidden="true"
  >
    <Circle />
    <div className="flex-1 space-y-2">
      <Line width="w-1/3" />
      <Line width="w-2/3" className="h-3" />
    </div>
  </div>
);
RowSkeleton.propTypes = { className: PropTypes.string };

const Skeleton = { Line, Circle, Card: CardSkeleton, Row: RowSkeleton };

export default Skeleton;
